// AI Helper utilities for search and chat
// Integrated with Flask AI service via WordPress REST API
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Get API URL from WordPress data
function getApiUrl() {
    const wpData = window.wpData;
    if (wpData === null || wpData === void 0 ? void 0 : wpData.apiUrl) {
        // Replace wp/v2/ with noads/v1/
        return wpData.apiUrl.replace('wp/v2/', 'noads/v1/');
    }
    return '/wp-json/noads/v1/';
}
// Get WordPress nonce for API authentication
function getNonce() {
    const wpData = window.wpData;
    return (wpData === null || wpData === void 0 ? void 0 : wpData.nonce) || '';
}
// AI search function - calls WordPress REST API which proxies to Flask service
export function searchWithAI(query) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        try {
            const apiUrl = getApiUrl();
            const nonce = getNonce();
            const response = yield fetch(`${apiUrl}ai/search`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': nonce,
                },
                body: JSON.stringify({ query }),
            });
            if (!response.ok) {
                const errorData = yield response.json().catch(() => ({}));
                throw new Error(errorData.message || `Search failed: ${response.status}`);
            }
            const data = yield response.json();
            // Ensure all required fields are present
            return {
                recipes: data.recipes || [],
                aiSummary: data.aiSummary || `Found ${((_a = data.recipes) === null || _a === void 0 ? void 0 : _a.length) || 0} recipes matching "${query}".`,
                suggestedRefinements: data.suggestedRefinements || [],
                relatedTopics: data.relatedTopics || []
            };
        }
        catch (error) {
            console.error('AI search error:', error);
            // Fallback to empty results with error message
            return {
                recipes: [],
                aiSummary: 'Sorry, I encountered an error while searching. Please try again.',
                suggestedRefinements: [],
                relatedTopics: []
            };
        }
    });
}
function getMatchReason(recipe, query) {
    var _a;
    if (recipe.name.toLowerCase().includes(query)) {
        return "Name matches your search";
    }
    if ((_a = recipe.category) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(query)) {
        return `Great ${recipe.category} option`;
    }
    return "Recommended for you";
}
function generateAISummary(query, resultCount) {
    if (resultCount === 0) {
        return "I couldn't find exact matches, but here are some similar recipes you might enjoy!";
    }
    if (query.toLowerCase().includes('quick') || query.toLowerCase().includes('easy')) {
        return `Found ${resultCount} quick and easy recipes. Most take 30 minutes or less!`;
    }
    if (query.toLowerCase().includes('vegetarian')) {
        return `Found ${resultCount} delicious vegetarian recipes. All are meat-free and full of flavor!`;
    }
    return `Found ${resultCount} recipes matching "${query}". Sorted by relevance.`;
}
function getSuggestedRefinements(query) {
    const refinements = [];
    if (!query.includes('vegetarian') && !query.includes('meat')) {
        refinements.push('Show only vegetarian');
    }
    if (!query.includes('quick') && !query.includes('time')) {
        refinements.push('Under 30 minutes');
    }
    if (!query.includes('easy') && !query.includes('difficult')) {
        refinements.push('Beginner-friendly (1-flame)');
    }
    return refinements;
}
function getRelatedTopics(query) {
    const topics = [];
    if (query.includes('pasta')) {
        topics.push({
            title: 'How to cook pasta al dente',
            description: 'Learn the secret to perfectly cooked pasta',
            type: 'technique'
        });
    }
    if (query.includes('chicken')) {
        topics.push({
            title: 'Chicken cooking temperatures',
            description: 'Safe cooking temps for poultry',
            type: 'technique'
        });
    }
    return topics;
}
// AI chat response function - calls WordPress REST API which proxies to Flask service
export function getChatResponse(userMessage, context) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const apiUrl = getApiUrl();
            const nonce = getNonce();
            const requestBody = {
                message: userMessage,
            };
            if (context) {
                requestBody.context = context;
            }
            const response = yield fetch(`${apiUrl}ai/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': nonce,
                },
                body: JSON.stringify(requestBody),
            });
            if (!response.ok) {
                const errorData = yield response.json().catch(() => ({}));
                throw new Error(errorData.message || `Chat failed: ${response.status}`);
            }
            const data = yield response.json();
            // Convert timestamp string to Date object
            const timestamp = data.timestamp ? new Date(data.timestamp) : new Date();
            // Ensure all required fields are present
            return {
                id: data.id || Date.now().toString(),
                role: data.role || 'assistant',
                content: data.content || "I'm here to help with cooking questions!",
                timestamp: timestamp,
                recipeCards: data.recipeCards || undefined,
                quickActions: data.quickActions || [
                    { label: '🔄 Substitutions', action: 'substitutions' },
                    { label: '🔪 Techniques', action: 'techniques' },
                    { label: '📏 Conversions', action: 'conversions' },
                    { label: '🍳 Recipe Help', action: 'recipe-help' }
                ]
            };
        }
        catch (error) {
            console.error('AI chat error:', error);
            // Fallback response
            return {
                id: Date.now().toString(),
                role: 'assistant',
                content: "Sorry, I encountered an error. Please try again or rephrase your question.",
                timestamp: new Date(),
                quickActions: [
                    { label: '🔄 Substitutions', action: 'substitutions' },
                    { label: '🔪 Techniques', action: 'techniques' },
                    { label: '📏 Conversions', action: 'conversions' },
                    { label: '🍳 Recipe Help', action: 'recipe-help' }
                ]
            };
        }
    });
}
function generateSubstitutionResponse(message, context) {
    const lowerMsg = message.toLowerCase();
    let content = "Great question! Here's what I'd suggest:\n\n";
    if (lowerMsg.includes('butter') && lowerMsg.includes('oil')) {
        content += "**Butter ↔ Oil Substitution**\n\n";
        content += "• **For Baking**: Use ¾ cup butter for 1 cup oil (or ⅞ cup for closer match)\n";
        content += "• **For Cooking**: 1:1 ratio works, but butter has a lower smoke point\n\n";
        content += "💡 **Tip**: For high-heat cooking, try clarified butter (ghee) - it won't burn as easily!";
    }
    else if (lowerMsg.includes('egg')) {
        content += "**Egg Substitutions**\n\n";
        content += "For 1 egg, try:\n";
        content += "• **Flax egg**: 1 tbsp ground flaxseed + 3 tbsp water (let sit 5 min)\n";
        content += "• **Applesauce**: ¼ cup (best for moist cakes)\n";
        content += "• **Mashed banana**: ¼ cup (adds banana flavor)\n";
        content += "• **Yogurt**: ¼ cup (great for binding)";
    }
    else if (lowerMsg.includes('milk')) {
        content += "**Milk Substitutions**\n\n";
        content += "Most recipes work 1:1 with:\n";
        content += "• Almond milk (lighter, slightly nutty)\n";
        content += "• Oat milk (creamy, neutral flavor)\n";
        content += "• Soy milk (protein-rich, best for savory)\n";
        content += "• Coconut milk (rich, adds coconut flavor)";
    }
    else {
        content += "I can help with substitutions! Common swaps include:\n\n";
        content += "• Dairy alternatives (milk, butter, cream)\n";
        content += "• Egg replacements\n";
        content += "• Flour types\n";
        content += "• Sweeteners\n\n";
        content += "What specific ingredient do you need to replace?";
    }
    return {
        id: Date.now().toString(),
        role: 'assistant',
        content,
        timestamp: new Date()
    };
}
function generateTechniqueResponse(message) {
    const lowerMsg = message.toLowerCase();
    let content = "";
    if (lowerMsg.includes('dice') || lowerMsg.includes('chop')) {
        content = "**How to Dice an Onion**\n\n";
        content += "1. Cut onion in half through root\n";
        content += "2. Peel and place flat side down\n";
        content += "3. Make horizontal cuts (don't cut through root)\n";
        content += "4. Make vertical cuts (perpendicular to board)\n";
        content += "5. Slice across - perfect dice!\n\n";
        content += "💡 **Tip**: Keep the root end intact to hold onion together while cutting.";
    }
    else if (lowerMsg.includes('sauté') || lowerMsg.includes('saute')) {
        content = "**Sautéing Basics**\n\n";
        content += "Sautéing = cooking quickly over medium-high heat with a small amount of fat\n\n";
        content += "**Keys to Success:**\n";
        content += "• Heat pan first, then add oil\n";
        content += "• Don't overcrowd the pan (food will steam, not sauté)\n";
        content += "• Keep food moving (that's what 'sauté' means!)\n";
        content += "• Pat food dry for better browning";
    }
    else if (lowerMsg.includes('boil')) {
        content = "**Boiling vs. Simmering**\n\n";
        content += "• **Rapid Boil**: Large bubbles, vigorous movement (212°F/100°C)\n";
        content += "  Good for: pasta, blanching vegetables\n\n";
        content += "• **Simmer**: Small bubbles, gentle movement (180-190°F)\n";
        content += "  Good for: soups, sauces, braising\n\n";
        content += "💡 **Tip**: Most recipes that say 'boil' really mean 'bring to a boil, then reduce to a simmer.'";
    }
    else {
        content = "I can help explain cooking techniques! Popular topics:\n\n";
        content += "• Knife skills (dicing, chopping, mincing)\n";
        content += "• Cooking methods (sautéing, braising, roasting)\n";
        content += "• Food safety (temperatures, storage)\n";
        content += "• Baking basics (measuring, mixing, proofing)\n\n";
        content += "What technique would you like to learn about?";
    }
    return {
        id: Date.now().toString(),
        role: 'assistant',
        content,
        timestamp: new Date()
    };
}
function generateRecipeSuggestion(message) {
    const lowerMsg = message.toLowerCase();
    const recipes = getAllRecipes();
    // Get 3-4 relevant recipes
    let suggestedRecipes = recipes.slice(0, 3);
    if (lowerMsg.includes('chicken')) {
        suggestedRecipes = recipes.filter(r => r.name.toLowerCase().includes('chicken')).slice(0, 3);
    }
    else if (lowerMsg.includes('pasta')) {
        suggestedRecipes = recipes.filter(r => r.name.toLowerCase().includes('pasta') || r.category === 'Pasta').slice(0, 3);
    }
    else if (lowerMsg.includes('easy') || lowerMsg.includes('beginner')) {
        suggestedRecipes = recipes.filter(r => r.difficulty === 'Easy').slice(0, 3);
    }
    let content = "I found some great recipes for you!\n\n";
    content += "Here are my top picks based on what you asked:";
    return {
        id: Date.now().toString(),
        role: 'assistant',
        content,
        timestamp: new Date(),
        recipeCards: suggestedRecipes.map(r => ({
            slug: r.slug,
            name: r.name,
            summary: r.summary || "",
            image: r.image
        })),
        quickActions: [
            { label: 'Show more recipes', action: 'show-more' },
            { label: 'Filter by time', action: 'filter-time' }
        ]
    };
}
function generateTroubleshootingResponse(message) {
    const lowerMsg = message.toLowerCase();
    let content = "Let's troubleshoot this! ";
    if (lowerMsg.includes('scrambled') && lowerMsg.includes('egg')) {
        content += "**Scrambled Eggs in Carbonara?**\n\n";
        content += "This is a common issue! Here's what happened:\n\n";
        content += "• The pasta or pan was too hot when you added the eggs\n";
        content += "• The eggs cooked too quickly and curdled\n\n";
        content += "**How to Fix It Next Time:**\n";
        content += "1. Remove pan from heat completely\n";
        content += "2. Let it cool for 30 seconds\n";
        content += "3. Add egg mixture while tossing constantly\n";
        content += "4. The residual heat will cook the eggs perfectly\n\n";
        content += "💡 **Pro tip**: Add a splash of pasta water to the eggs before mixing - it helps create a creamy sauce!";
    }
    else if (lowerMsg.includes('burned') || lowerMsg.includes('burnt')) {
        content += "**Something Burned?**\n\n";
        content += "Common causes:\n";
        content += "• Heat too high (try medium or medium-high)\n";
        content += "• Not enough fat/oil in the pan\n";
        content += "• Not stirring frequently enough\n";
        content += "• Pan was too hot before adding ingredients\n\n";
        content += "**Prevention:**\n";
        content += "• Preheat pan gradually\n";
        content += "• Keep spatula handy for stirring\n";
        content += "• Don't walk away during high-heat cooking!";
    }
    else if (lowerMsg.includes('dry') || lowerMsg.includes('overcooked')) {
        content += "**Food Came Out Dry?**\n\n";
        content += "Possible reasons:\n";
        content += "• Cooked too long\n";
        content += "• Temperature too high\n";
        content += "• Not enough liquid/fat\n\n";
        content += "**Tips for Next Time:**\n";
        content += "• Use a meat thermometer for proteins\n";
        content += "• Cover pan to retain moisture\n";
        content += "• Let meat rest before cutting (juices redistribute)";
    }
    else {
        content += "I'm here to help troubleshoot! Common issues:\n\n";
        content += "• Burned or overcooked food\n";
        content += "• Eggs scrambling in sauces\n";
        content += "• Sauce too thick or thin\n";
        content += "• Dry meat or overcooked pasta\n\n";
        content += "Tell me what went wrong and I'll help you fix it!";
    }
    return {
        id: Date.now().toString(),
        role: 'assistant',
        content,
        timestamp: new Date()
    };
}
// Get quick help suggestions based on context
export function getQuickHelpSuggestions(context) {
    if (context === null || context === void 0 ? void 0 : context.recipeName) {
        return [
            `How do I scale ${context.recipeName}?`,
            'What are good substitutions?',
            'Common mistakes to avoid?',
            'What equipment do I need?'
        ];
    }
    return [
        'Find recipes with ingredients I have',
        'Teach me a cooking technique',
        'How do I substitute ingredients?',
        'Show me easy beginner recipes'
    ];
}
// Store conversation history in localStorage
const STORAGE_KEY = 'noAdsCooking_chatHistory';
export function saveChatHistory(messages) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    }
    catch (error) {
        console.error('Failed to save chat history:', error);
    }
}
export function loadChatHistory() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored)
            return [];
        const messages = JSON.parse(stored);
        // Convert timestamp strings back to Date objects
        return messages.map((msg) => (Object.assign(Object.assign({}, msg), { timestamp: new Date(msg.timestamp) })));
    }
    catch (error) {
        console.error('Failed to load chat history:', error);
        return [];
    }
}
export function clearChatHistory() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    }
    catch (error) {
        console.error('Failed to clear chat history:', error);
    }
}
